"use client";

import React from "react";
import { ShieldCheck, Award, Building2, House as Home, Store, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const localCapabilities = [{
  icon: Building2,
  title: "Town-centre & office premises",
  desc: "Pedestrian gantries, facade access, and scheduled out-of-hours erection in busy Basingstoke commercial zones."
}, {
  icon: Home,
  title: "Residential housing estates",
  desc: "Safe perimeter access, temporary roofs, and edge protection for local housing developments and private builds."
}, {
  icon: Store,
  title: "Retail parks & commercial units",
  desc: "Robust structures tailored for retail maintenance, solar arrays, cladding, and mechanical installations."
}];
export default function BasingstokeIntro() {
  const prefersReducedMotion = useReducedMotion();
  const motionInitial = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: 0.5,
    ease: "easeOut"
  };
  return <section id="basingstokeintro" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Main Copy Column */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true,
          amount: 0.2
        }} transition={motionTransition} className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Accreditation Badges */}
            <div className="flex flex-wrap items-center gap-2.5 mb-5">
              <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/30 bg-primary/5">
                <Award className="size-3.5 mr-1" />
                NASC Accredited
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground border-border bg-muted">
                <ShieldCheck className="size-3.5 mr-1 text-primary" />
                CHAS Certified
              </Badge>
              <Badge variant="secondary" className="px-3 py-1 text-xs font-medium">
                20+ Years Industry Experience
              </Badge>
            </div>

            {/* Answer-First H1 Headline */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default mb-6 leading-tight">
              Scaffolding in Basingstoke
            </h2>

            {/* Answer-First Copy */}
            <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p>
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Basingstoke and the surrounding Hampshire area, including RG21 to RG24. Led by Pete Wood with over 20 years of hands-on scaffolding experience, our team brings serious operational safety and family-run accountability to every project.
              </p>
              <p>
                From town-centre commercial offices and busy retail parks to extensive residential housing estates, we design, erect, and inspect compliant access structures. Fully accredited by NASC and CHAS, we ensure every structure meets rigorous safety standards, arrives on schedule, and supports your trades without delays.
              </p>
            </div>

            {/* Feature Checkpoints */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 mb-8 w-full">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>NASC and CHAS safety certified</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Led by Pete Wood (20+ years)</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Prompt site surveys in Basingstoke</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Commercial, domestic & solar access</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">
                  Request a quote
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/HealthSafetyAccreditationsPage">
                  View accreditations
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Local Sector Capability Cards */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          ...motionTransition,
          delay: 0.15
        }} className="lg:col-span-5 flex flex-col gap-4">
            <div className="p-1">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Local Basingstoke coverage
              </h2>
              <div className="flex flex-col gap-4">
                {localCapabilities.map((item, i) => {
                const IconComp = item.icon;
                return <Card key={item.title} data-index={i} className="border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                      <CardContent className="p-5 flex items-start gap-4">
                        <div className="size-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                          <IconComp className="size-5" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-card-foreground mb-1">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </CardContent>
                    </Card>;
              })}
              </div>

              {/* Coverage Note Box */}
              <div className="mt-4 p-4 rounded-md bg-muted/60 border border-border/80 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Serving Basingstoke, Tadley, Hook, Oakley & North Hampshire.
                </span>
                <Button asChild variant="link" size="sm" className="text-xs font-semibold p-0 h-auto">
                  <Link to="/AreasWeCoverPage">
                    All areas
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}